import { ColDef } from '@ag-grid-community/core';
import { Box, Stack } from '@atgfr8/atg-ui-tailwind';
import { Notification, ToastType } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import { DocumentEntityType } from '@components/FileUpload';
import { useParams } from '@solidjs/router';
import {
  getPricingData,
  getPricingEnabled,
  pricingStore,
  setPricingStore,
  togglePricing,
} from '@store/customers/customerDetails/pricingTab';
import { formatAmount, handleToast } from '@utils/utils';
import { createEffect, createSignal, onMount, Show } from 'solid-js';

import { agGridCellStyle } from '../addressBookTab/AddressBookTab';
import classes from './classes';
import PricingSheetGrid from './PricingSheetGrid';

const Pricing = () => {
  const [pricingWarningOpen, setPricingWarningOpen] = createSignal(true);
  const [isToggleLoading, setIsToggleLoading] = createSignal(false);
  const params = useParams();

  const handleToglePricing = async (checked: boolean) => {
    setIsToggleLoading(true);
    const res = await togglePricing({
      CustomerId: Number(params.id),
      Enabled: checked,
    });
    if (res && res.isSuccess) {
      setPricingStore('isPricingEnabled', checked);
      handleToast(
        ToastType.Success,
        `Pricing ${checked === true ? 'Enabled' : 'Disabled'} successfully`,
      );
    }
    setIsToggleLoading(false);
  };

  const ColumnDefs: ColDef[] = [
    {
      field: 'originZip',
      headerName: 'Origin Zip',
      minWidth: 100,
    },
    {
      field: 'originCity',
      headerName: 'Origin City',
      minWidth: 150,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'originState',
      headerName: 'Origin State',
      minWidth: 80,
    },
    {
      field: 'destinationZip',
      headerName: 'Destination Zip',
      minWidth: 100,
    },
    {
      field: 'destinationCity',
      headerName: 'Destination City',
      minWidth: 150,
      cellStyle: agGridCellStyle,
    },
    {
      field: 'destinationState',
      headerName: 'Destination State',
      minWidth: 80,
    },

    {
      field: 'miles',
      headerName: 'Miles',
      minWidth: 100,
    },
    {
      field: 'equipment',
      headerName: 'Equipment',
      cellStyle: agGridCellStyle,
      minWidth: 100,
    },
    {
      field: 'rateType',
      headerName: 'Rate Type',
      cellStyle: agGridCellStyle,
      minWidth: 100,
    },
    {
      field: 'currency',
      headerName: 'Currency',
      cellStyle: agGridCellStyle,
      minWidth: 80,
    },
    {
      field: 'rate',
      headerName: 'Rate',
      minWidth: 150,
      valueFormatter: (params) => {
        return formatAmount(params.value as number);
      },
    },
  ];

  onMount(async () => {
    setPricingStore('isLoading', true);
    await getPricingData(Number(params.id));
    await getPricingEnabled(Number(params.id));
    setPricingStore('isLoading', false);
  });
  let previousId = params.id;
  createEffect(async () => {
    if (previousId !== params.id) {
      previousId = params.id;
      setPricingStore('isLoading', true);
      await getPricingData(Number(params.id));
      await getPricingEnabled(Number(params.id));
      setPricingStore('isLoading', false);
    }
  });

  return (
    <Stack class={classes.container}>
      <Show when={isToggleLoading()}>
        <AtgLoader />
      </Show>
      <Show when={!pricingStore.isLoading} fallback={<AtgLoader />}>
        <Box class={classes.pricingSheetGridBox}>
          <Show when={pricingWarningOpen()}>
            <Notification
              type="warning"
              text={
                <p>
                  <strong>WARNING:</strong> Failure to upload updated pricing
                  sheets may result in a loss of profit because you may be
                  locked into a rate that you will be required to honor.
                </p>
              }
              onClose={() => setPricingWarningOpen(false)}
            />
          </Show>
          <Box class={classes.gridBox}>
            <PricingSheetGrid
              pricingSheetColumnDefs={ColumnDefs}
              items={pricingStore.items}
              handleToglePricing={handleToglePricing}
              loading={pricingStore.isLoading}
              isToggleLoading={isToggleLoading()}
              pricingFileUploadProps={{
                documentEntityType: DocumentEntityType.Load,
                entityId: Number(params.id),
              }}
            />
          </Box>
        </Box>
      </Show>
    </Stack>
  );
};

export default Pricing;
